import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import { Icon, type IconName } from './Icon'
import './Plate.css'

export interface PlateProps {
  /** Resolved (signed) image URL. Null renders the empty mount. */
  src?: string | null
  alt?: string
  name?: string | null
  caption?: string
  to?: string
  onClick?: () => void
  shape?: 'portrait' | 'square' | 'wide'
  width?: 'rail' | 'rail-lg' | 'fill'
  /** Small marks pinned into the frame: sighting count, video, pending sync. */
  marks?: ReactNode
  className?: string
}

const UNNAMED = 'Sin nombre'

export function Plate({
  src,
  alt,
  name,
  caption,
  to,
  onClick,
  shape = 'portrait',
  width = 'fill',
  marks,
  className,
}: PlateProps) {
  const [loaded, setLoaded] = useState(false)
  // A photograph that cannot be fetched (no network, a blocked host) shows
  // the empty mount rather than an invisible, broken image.
  const [failed, setFailed] = useState(false)
  const named = Boolean(name)

  const inner = (
    <>
      <div className="plate__frame">
        {src && !failed ? (
          <img
            className="plate__img"
            src={src}
            alt={alt ?? (named ? 'Fotografía de ' + name : 'Fotografía del gato')}
            loading="lazy"
            decoding="async"
            data-loaded={loaded}
            onLoad={() => setLoaded(true)}
            onError={() => setFailed(true)}
          />
        ) : (
          <div className="plate__empty">
            <Icon name="cat" size={28} />
          </div>
        )}
        {marks ? <div className="plate__marks">{marks}</div> : null}
      </div>
      {(name !== undefined || caption) && (
        <div className="plate__body">
          {name !== undefined && (
            <span className={clsx('plate__name', !named && 'plate__name--unnamed')}>
              {named ? name : UNNAMED}
            </span>
          )}
          {caption && <span className="plate__caption">{caption}</span>}
        </div>
      )}
    </>
  )

  const cls = clsx(
    'plate',
    shape === 'square' && 'plate--square',
    shape === 'wide' && 'plate--wide',
    width !== 'fill' && 'plate--' + width,
    className,
  )

  if (to) {
    return (
      <Link to={to} className={cls}>
        {inner}
      </Link>
    )
  }
  if (onClick) {
    return (
      <button type="button" className={cls} onClick={onClick}>
        {inner}
      </button>
    )
  }
  return <div className={cls}>{inner}</div>
}

/** A mark pinned inside a plate frame. */
export function PlateMark({
  icon,
  children,
  tone,
}: {
  icon?: IconName
  children?: ReactNode
  tone?: 'accent' | 'pending'
}) {
  return (
    <span className={clsx('plate__mark', tone && 'plate__mark--' + tone)}>
      {icon && <Icon name={icon} size={12} strokeWidth={2.4} />}
      {children}
    </span>
  )
}

/** The full-bleed hero: the photograph is the page, the name is inscribed on it. */
export function HeroPlate({
  src,
  alt,
  name,
  meta,
  pages,
  activePage,
  onSelectPage,
  children,
}: {
  src?: string | null
  alt?: string
  name?: string | null
  meta?: ReactNode
  pages?: number
  activePage?: number
  onSelectPage?: (i: number) => void
  children?: ReactNode
}) {
  const named = Boolean(name)
  return (
    <div className="hero-plate">
      {src ? (
        <img className="hero-plate__img" src={src} alt={alt ?? name ?? 'Gato'} decoding="async" />
      ) : (
        <div className="plate__empty">
          <Icon name="cat" size={48} />
        </div>
      )}
      <div className="hero-plate__scrim" />
      {pages && pages > 1 ? (
        <div className="hero-plate__pager" role="tablist" aria-label="Fotografías">
          {Array.from({ length: pages }, (_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === activePage}
              aria-label={'Fotografía ' + (i + 1)}
              className="hero-plate__dot"
              data-active={i === activePage}
              onClick={() => onSelectPage?.(i)}
            />
          ))}
        </div>
      ) : null}
      <div className="hero-plate__inscription">
        <h1 className={clsx('hero-plate__name', !named && 'hero-plate__name--unnamed')}>
          {named ? name : UNNAMED}
        </h1>
        {meta && <div className="hero-plate__meta">{meta}</div>}
        {children}
      </div>
    </div>
  )
}
